package com.kanati.admin.app

import androidx.compose.runtime.key
import com.kanati.admin.features.readiness.ReadinessPanel
import com.kanati.admin.features.settings.ConsumerConfigPanel
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.api.AdminApiException
import com.kanati.admin.components.AppShell
import com.kanati.admin.features.auth.LoginScreen
import com.kanati.admin.features.dashboard.DashboardScreen
import com.kanati.admin.features.events.EventsScreen
import com.kanati.admin.features.inventory.InventoryScreen
import com.kanati.admin.features.loyalty.LoyaltyScreen
import com.kanati.admin.features.platform.PlatformDashboardScreen
import com.kanati.admin.features.products.ProductsScreen
import com.kanati.admin.features.reports.ReportsScreen
import com.kanati.admin.features.schools.SchoolsScreen
import com.kanati.admin.features.settings.SettingsScreen
import com.kanati.admin.features.users.UsersScreen
import com.kanati.admin.state.AdminSessionTokenStore
import com.kanati.admin.state.DistrictContextState
import com.kanati.admin.state.StaticThemePackSource
import com.kanati.admin.state.ThemeMode
import com.kanati.admin.state.accessibleSchoolsForDistrict
import com.kanati.admin.state.accessibleSchoolsByDistrict
import com.kanati.admin.state.authorizedDistricts
import com.kanati.admin.state.hasDistrictAdminAccess
import com.kanati.admin.state.hasSchoolAdminAccess
import com.kanati.admin.state.paletteFor
import com.kanati.admin.state.toSelectedSchoolContext
import com.kanati.shared.admin.CurrentOperatorContextResponse
import com.kanati.shared.admin.LoginRequest
import com.kanati.shared.admin.OperatorRole
import com.kanati.shared.district.District
import com.kanati.shared.school.School
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeout

private const val ROUTE_TRANSITION_MIN_MS = 260L
private const val AUTH_FLOW_TIMEOUT_MS = 15_000L
private const val SIGN_IN_TIMEOUT_MESSAGE =
    "Signing in took longer than expected. Please try again. If this keeps happening, verify the admin API is reachable from this browser."
private const val LOAD_ACCESS_TIMEOUT_MESSAGE =
    "Loading your admin access took longer than expected. Please try again. If this keeps happening, verify the admin API allows this browser origin to complete authenticated requests."
private const val LOAD_PLATFORM_DISTRICTS_TIMEOUT_MESSAGE =
    "Loading platform districts took longer than expected. Refresh the context and try again."
private const val RESTORE_SESSION_TIMEOUT_MESSAGE =
    "Restoring your admin session took longer than expected. Sign in again to continue."

@Composable
fun AdminApp() {
    var route by remember { mutableStateOf(AdminRoute.Dashboard) }
    var displayedRoute by remember { mutableStateOf(AdminRoute.Dashboard) }
    var isRouteTransitioning by remember { mutableStateOf(false) }
    var themeMode by remember { mutableStateOf(ThemeMode.Light) }
    var districtContext by remember { mutableStateOf(DistrictContextState()) }
    var currentOperatorContext by remember { mutableStateOf<CurrentOperatorContextResponse?>(null) }
    var accessToken by remember { mutableStateOf(AdminSessionTokenStore.load()) }
    var authError by remember { mutableStateOf<String?>(null) }
    var authProgressMessage by remember { mutableStateOf<String?>(null) }
    var isAuthenticating by remember { mutableStateOf(false) }
    var isRestoringSession by remember { mutableStateOf(accessToken != null) }
    var loginEmail by remember { mutableStateOf("") }
    var loginPassword by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()
    val apiClient = remember { AdminApiClient(baseUrl = AdminApiConfig.DEFAULT_BASE_URL) }
    val themePackSource = remember { StaticThemePackSource }
    val themePack = remember(themePackSource) { themePackSource.getThemePack() }
    val theme = themePack.paletteFor(themeMode)

    apiClient.accessToken = accessToken

    fun resetRoutes(availableRoutes: List<AdminRoute> = listOf(AdminRoute.Dashboard)) {
        val fallbackRoute = availableRoutes.firstOrNull() ?: AdminRoute.Dashboard
        route = fallbackRoute
        displayedRoute = fallbackRoute
        isRouteTransitioning = false
    }

    fun resolvedSelectedSchoolId(
        schools: List<School>,
        preservedSchoolId: String?,
    ): String? = when {
        schools.isEmpty() -> null
        schools.size == 1 -> schools.single().id
        preservedSchoolId != null && schools.any { it.id == preservedSchoolId } -> preservedSchoolId
        else -> null
    }

    fun buildDistrictContextState(
        context: CurrentOperatorContextResponse,
        districts: List<District>,
        error: String? = null,
    ): DistrictContextState {
        val accessScopedSchoolsByDistrictId = context.accessibleSchoolsByDistrict()
        val schoolsByDistrictId = buildMap {
            (districts.map(District::id) + districtContext.schoolsByDistrictId.keys + accessScopedSchoolsByDistrictId.keys)
                .distinct()
                .forEach { districtId ->
                    val knownSchools = (districtContext.schoolsByDistrictId[districtId].orEmpty() +
                        accessScopedSchoolsByDistrictId[districtId].orEmpty())
                        .distinctBy(School::id)
                        .sortedBy { it.name.lowercase() }
                    if (knownSchools.isNotEmpty()) {
                        put(districtId, knownSchools)
                    }
                }
        }
        val preservedDistrictId = districtContext.selectedDistrictId
            ?.takeIf { selectedId -> districts.any { it.id == selectedId } }
        val selectedDistrictId = when {
            districts.size == 1 -> districts.single().id
            preservedDistrictId != null -> preservedDistrictId
            else -> null
        }
        val selectedSchoolIdByDistrictId = schoolsByDistrictId.mapNotNull { (districtId, schools) ->
            val accessibleSchools = context.accessibleSchoolsForDistrict(
                districtId = districtId,
                knownSchools = schools,
            )
            resolvedSelectedSchoolId(
                schools = accessibleSchools,
                preservedSchoolId = districtContext.selectedSchoolIdByDistrictId[districtId],
            )?.let { schoolId -> districtId to schoolId }
        }.toMap()

        return DistrictContextState(
            districts = districts,
            schoolsByDistrictId = schoolsByDistrictId,
            selectedDistrictId = selectedDistrictId,
            selectedSchoolIdByDistrictId = selectedSchoolIdByDistrictId,
            isLoading = false,
            error = error ?: if (context.operator.role != OperatorRole.SUPERADMIN && districts.isEmpty()) {
                "No district or school access is assigned to ${context.operator.displayName}."
            } else {
                null
            },
        )
    }

    fun applyOperatorContext(
        context: CurrentOperatorContextResponse,
        districts: List<District>,
        error: String? = null,
    ) {
        currentOperatorContext = context
        districtContext = buildDistrictContextState(
            context = context,
            districts = districts,
            error = error,
        )
    }

    fun hasAssignedAccess(context: CurrentOperatorContextResponse): Boolean =
        context.districtAccess.isNotEmpty() || context.schoolAccess.isNotEmpty() || context.operableSchools.isNotEmpty()

    fun clearSession(message: String? = null, clearPassword: Boolean = true) {
        accessToken = null
        apiClient.accessToken = null
        AdminSessionTokenStore.clear()
        currentOperatorContext = null
        districtContext = DistrictContextState()
        authError = message
        authProgressMessage = null
        if (clearPassword) {
            loginPassword = ""
        }
        isAuthenticating = false
        isRestoringSession = false
        resetRoutes()
    }

    suspend fun loadCurrentOperatorContext(
        showSessionErrorOnUnauthorized: Boolean = true,
        timeoutMessage: String = LOAD_ACCESS_TIMEOUT_MESSAGE,
        clearPasswordOnFailure: Boolean = true,
    ) {
        if (accessToken.isNullOrBlank()) {
            clearSession()
            return
        }

        districtContext = districtContext.copy(isLoading = true, error = null)
        try {
            val fetchedContext = withTimeout(AUTH_FLOW_TIMEOUT_MS) {
                apiClient.getCurrentOperatorContext()
            }
            authError = null
            val shouldProbePlatformAccess =
                fetchedContext.operator.role == OperatorRole.SUPERADMIN || !hasAssignedAccess(fetchedContext)

            if (shouldProbePlatformAccess) {
                try {
                    val districts = withTimeout(AUTH_FLOW_TIMEOUT_MS) {
                        apiClient.getDistricts().sortedBy { it.name.lowercase() }
                    }
                    val inferredContext = if (fetchedContext.operator.role == OperatorRole.SUPERADMIN) {
                        fetchedContext
                    } else {
                        fetchedContext.copy(
                            operator = fetchedContext.operator.copy(role = OperatorRole.SUPERADMIN),
                        )
                    }
                    applyOperatorContext(context = inferredContext, districts = districts)
                } catch (_: TimeoutCancellationException) {
                    if (fetchedContext.operator.role == OperatorRole.SUPERADMIN) {
                        applyOperatorContext(
                            context = fetchedContext,
                            districts = districtContext.districts,
                            error = LOAD_PLATFORM_DISTRICTS_TIMEOUT_MESSAGE,
                        )
                    } else {
                        applyOperatorContext(
                            context = fetchedContext,
                            districts = fetchedContext.authorizedDistricts(),
                        )
                    }
                } catch (exception: Exception) {
                    if (exception is AdminApiException && exception.statusCode == 401) {
                        throw exception
                    }
                    if (fetchedContext.operator.role == OperatorRole.SUPERADMIN) {
                        applyOperatorContext(
                            context = fetchedContext,
                            districts = districtContext.districts,
                            error = exception.message ?: "Unable to load platform districts.",
                        )
                    } else {
                        applyOperatorContext(
                            context = fetchedContext,
                            districts = fetchedContext.authorizedDistricts(),
                        )
                    }
                }
            } else {
                applyOperatorContext(
                    context = fetchedContext,
                    districts = fetchedContext.authorizedDistricts(),
                )
            }
        } catch (_: TimeoutCancellationException) {
            clearSession(
                message = timeoutMessage,
                clearPassword = clearPasswordOnFailure,
            )
        } catch (exception: Exception) {
            if (exception is AdminApiException && exception.statusCode == 401) {
                clearSession(
                    if (showSessionErrorOnUnauthorized) {
                        "Your admin session is not available. Sign in again."
                    } else {
                        null
                    },
                    clearPassword = clearPasswordOnFailure,
                )
            } else {
                clearSession(
                    message = exception.message ?: "Unable to load your admin access. Please try again.",
                    clearPassword = clearPasswordOnFailure,
                )
            }
        } finally {
            isRestoringSession = false
            authProgressMessage = null
        }
    }

    LaunchedEffect(Unit) {
        if (!accessToken.isNullOrBlank()) {
            authProgressMessage = "Restoring your admin session..."
            loadCurrentOperatorContext(
                showSessionErrorOnUnauthorized = false,
                timeoutMessage = RESTORE_SESSION_TIMEOUT_MESSAGE,
            )
        } else {
            isRestoringSession = false
        }
    }

    LaunchedEffect(route, currentOperatorContext != null) {
        if (currentOperatorContext == null || route == displayedRoute) {
            isRouteTransitioning = false
            return@LaunchedEffect
        }

        isRouteTransitioning = true
        delay(ROUTE_TRANSITION_MIN_MS)
        displayedRoute = route
        isRouteTransitioning = false
    }

    fun login() {
        when {
            loginEmail.isBlank() -> authError = "Email is required."
            loginPassword.isBlank() -> authError = "Password is required."
            else -> scope.launch {
                isAuthenticating = true
                isRestoringSession = false
                authError = null
                authProgressMessage = "Signing in and loading your admin access..."
                try {
                    val response = withTimeout(AUTH_FLOW_TIMEOUT_MS) {
                        apiClient.login(
                            LoginRequest(
                                email = loginEmail.trim(),
                                password = loginPassword,
                            )
                        )
                    }
                    accessToken = response.accessToken
                    apiClient.accessToken = response.accessToken
                    AdminSessionTokenStore.save(response.accessToken)
                    authProgressMessage = "Signed in. Loading your admin access..."
                    loadCurrentOperatorContext(clearPasswordOnFailure = false)
                } catch (_: TimeoutCancellationException) {
                    clearSession(
                        message = SIGN_IN_TIMEOUT_MESSAGE,
                        clearPassword = false,
                    )
                } catch (exception: Exception) {
                    accessToken = null
                    apiClient.accessToken = null
                    AdminSessionTokenStore.clear()
                    currentOperatorContext = null
                    districtContext = DistrictContextState()
                    authError = exception.message ?: "Unable to sign in."
                    authProgressMessage = null
                } finally {
                    isAuthenticating = false
                    authProgressMessage = null
                }
            }
        }
    }

    val operatorContext = currentOperatorContext
    if (operatorContext == null) {
        LoginScreen(
            email = loginEmail,
            password = loginPassword,
            error = authError,
            progressMessage = authProgressMessage,
            isSubmitting = isAuthenticating,
            isRestoringSession = isRestoringSession,
            theme = theme,
            themeMode = themeMode,
            onEmailChange = { loginEmail = it },
            onPasswordChange = { loginPassword = it },
            onLogin = ::login,
            onToggleTheme = {
                themeMode = if (themeMode == ThemeMode.Light) ThemeMode.Dark else ThemeMode.Light
            },
        )
        return
    }

    val currentDistrict = districtContext.currentDistrict
    val isSuperadmin = operatorContext.operator.role == OperatorRole.SUPERADMIN
    val availableSchools = currentDistrict?.id?.let { districtId ->
        operatorContext.accessibleSchoolsForDistrict(
            districtId = districtId,
            knownSchools = districtContext.schoolsByDistrictId[districtId].orEmpty(),
        )
    }.orEmpty()
    val currentSchool = availableSchools
        .firstOrNull { it.id == districtContext.selectedSchoolId }
        ?.toSelectedSchoolContext()
    val hasAssignedDistrictAdminAccess = currentDistrict?.id?.let(operatorContext::hasDistrictAdminAccess) == true
    val districtAdminAccess = hasAssignedDistrictAdminAccess || (isSuperadmin && currentDistrict != null)
    val schoolAdminAccess = if (currentDistrict != null && currentSchool != null) {
        operatorContext.hasSchoolAdminAccess(currentDistrict.id, currentSchool.id)
    } else {
        false
    }
    val availableRoutes = buildList {
        if (isSuperadmin) {
            add(AdminRoute.Dashboard)
        }
        if (availableSchools.isNotEmpty() && schoolAdminAccess) {
            if (AdminRoute.Dashboard !in this) {
                add(AdminRoute.Dashboard)
            }
            add(AdminRoute.Products)
            add(AdminRoute.Inventory)
            add(AdminRoute.Events)
            add(AdminRoute.Loyalty)
            add(AdminRoute.Reports)
        }
        if (districtAdminAccess) {
            add(AdminRoute.Settings)
            add(AdminRoute.Users)
            if (hasAssignedDistrictAdminAccess) {
                add(AdminRoute.Schools)
            }
        }
    }

    LaunchedEffect(availableRoutes, currentOperatorContext != null) {
        if (currentOperatorContext != null && route !in availableRoutes) {
            resetRoutes(availableRoutes)
        }
    }

    fun applyDistrictTaxRateUpdate(defaultTaxRateBps: Int) {
        val selectedDistrictId = districtContext.selectedDistrictId ?: return
        districtContext = districtContext.copy(
            districts = districtContext.districts.map { district ->
                if (district.id == selectedDistrictId) {
                    district.copy(defaultTaxRateBps = defaultTaxRateBps)
                } else {
                    district
                }
            }
        )
    }

    fun updateSelectedDistrict(districtId: String) {
        val normalizedDistrictId = districtId.takeIf(String::isNotBlank)
        val selectedSchoolIdByDistrictId = districtContext.selectedSchoolIdByDistrictId.toMutableMap()
        normalizedDistrictId?.let { nextDistrictId ->
            val selectedSchoolId = resolvedSelectedSchoolId(
                schools = operatorContext.accessibleSchoolsForDistrict(
                    districtId = nextDistrictId,
                    knownSchools = districtContext.schoolsByDistrictId[nextDistrictId].orEmpty(),
                ),
                preservedSchoolId = selectedSchoolIdByDistrictId[nextDistrictId],
            )
            if (selectedSchoolId != null) {
                selectedSchoolIdByDistrictId[nextDistrictId] = selectedSchoolId
            } else {
                selectedSchoolIdByDistrictId.remove(nextDistrictId)
            }
        }

        districtContext = districtContext.copy(
            selectedDistrictId = normalizedDistrictId,
            selectedSchoolIdByDistrictId = selectedSchoolIdByDistrictId,
        )
    }

    fun updateSelectedSchool(schoolId: String) {
        val districtId = districtContext.selectedDistrictId ?: return
        val updated = districtContext.selectedSchoolIdByDistrictId.toMutableMap()
        schoolId.takeIf(String::isNotBlank)?.let { updated[districtId] = it } ?: updated.remove(districtId)
        districtContext = districtContext.copy(selectedSchoolIdByDistrictId = updated)
    }

    AppShell(
        activeRoute = route,
        availableRoutes = availableRoutes,
        availableSchools = availableSchools,
        currentOperator = operatorContext.operator,
        districtAdminAccess = districtAdminAccess,
        schoolAdminAccess = schoolAdminAccess,
        districtContext = districtContext,
        currentDistrict = currentDistrict,
        currentSchool = currentSchool,
        theme = theme,
        themeMode = themeMode,
        onDistrictSelected = ::updateSelectedDistrict,
        onSchoolSelected = ::updateSelectedSchool,
        onReloadContext = {
            scope.launch {
                loadCurrentOperatorContext()
            }
        },
        onRouteSelected = { route = it },
        onToggleTheme = {
            themeMode = if (themeMode == ThemeMode.Light) ThemeMode.Dark else ThemeMode.Light
        },
        onLogout = {
            scope.launch {
                runCatching { apiClient.logout() }
                clearSession()
            }
        },
        isRouteTransitioning = isRouteTransitioning,
        ) {
        key(currentDistrict?.id, currentSchool?.id) {
        when (displayedRoute) {
            AdminRoute.Dashboard -> {
                ReadinessPanel(apiClient, theme)
                currentSchool?.let { ConsumerConfigPanel(apiClient, it, theme) }
                if (isSuperadmin && currentSchool == null) {
                PlatformDashboardScreen(
                    apiClient = apiClient,
                    districts = districtContext.districts,
                    selectedDistrict = currentDistrict,
                    districtLoading = districtContext.isLoading,
                    districtLoadError = districtContext.error,
                    theme = theme,
                    onDistrictSelected = ::updateSelectedDistrict,
                    onReloadContext = {
                        scope.launch {
                            loadCurrentOperatorContext()
                        }
                    },
                    onDistrictCreated = { district ->
                        updateSelectedDistrict(district.id)
                        scope.launch {
                            loadCurrentOperatorContext()
                        }
                    },
                    onInitialAdminCreated = { districtId ->
                        updateSelectedDistrict(districtId)
                        scope.launch {
                            loadCurrentOperatorContext()
                            route = AdminRoute.Settings
                        }
                    },
                )
            } else {
                DashboardScreen(
                    apiClient = apiClient,
                    district = currentDistrict,
                    school = currentSchool,
                    districtLoading = districtContext.isLoading,
                    districtLoadError = districtContext.error,
                    today = kotlin.js.Date().toISOString().take(10),
                    theme = theme,
                    onNavigateToInventory = { route = AdminRoute.Inventory },
                )
            }

            }

            AdminRoute.Products -> ProductsScreen(
                apiClient = apiClient,
                district = currentDistrict,
                school = currentSchool,
                districtLoading = districtContext.isLoading,
                districtLoadError = districtContext.error,
                theme = theme,
                canEdit = schoolAdminAccess,
            )

            AdminRoute.Inventory -> InventoryScreen(
                apiClient = apiClient,
                district = currentDistrict,
                school = currentSchool,
                districtLoading = districtContext.isLoading,
                districtLoadError = districtContext.error,
                theme = theme,
                canEdit = schoolAdminAccess,
            )

            AdminRoute.Events -> EventsScreen(
                canValidate = operatorContext.operableSchools.any { it.school.id == currentSchool?.id && it.posAccess },
                apiClient = apiClient,
                district = currentDistrict,
                school = currentSchool,
                districtLoading = districtContext.isLoading,
                districtLoadError = districtContext.error,
                theme = theme,
                canEdit = schoolAdminAccess,
            )

            AdminRoute.Loyalty -> LoyaltyScreen(
                apiClient = apiClient,
                district = currentDistrict,
                school = currentSchool,
                districtLoading = districtContext.isLoading,
                districtLoadError = districtContext.error,
                theme = theme,
                canEdit = schoolAdminAccess,
            )

            AdminRoute.Reports -> ReportsScreen(
                apiClient = apiClient,
                district = currentDistrict,
                school = currentSchool,
                districtLoading = districtContext.isLoading,
                districtLoadError = districtContext.error,
                theme = theme,
            )

            AdminRoute.Settings -> SettingsScreen(
                apiClient = apiClient,
                district = currentDistrict,
                districtLoading = districtContext.isLoading,
                districtLoadError = districtContext.error,
                theme = theme,
                onDistrictTaxRateUpdated = ::applyDistrictTaxRateUpdate,
                canEdit = districtAdminAccess,
            )

            AdminRoute.Schools -> SchoolsScreen(
                apiClient = apiClient,
                district = currentDistrict,
                districtLoading = districtContext.isLoading,
                districtLoadError = districtContext.error,
                theme = theme,
                canEdit = districtAdminAccess,
                onSchoolsChanged = { schools ->
                    val districtId = currentDistrict?.id ?: return@SchoolsScreen
                    val updatedSchoolsByDistrictId = districtContext.schoolsByDistrictId.toMutableMap()
                    updatedSchoolsByDistrictId[districtId] = schools.sortedBy { it.name.lowercase() }
                    val updatedSchoolSelections = districtContext.selectedSchoolIdByDistrictId.toMutableMap()
                    val accessibleSchools = operatorContext.accessibleSchoolsForDistrict(
                        districtId = districtId,
                        knownSchools = schools,
                    )
                    val selectedSchoolId = resolvedSelectedSchoolId(
                        schools = accessibleSchools,
                        preservedSchoolId = updatedSchoolSelections[districtId],
                    )
                    if (selectedSchoolId != null) {
                        updatedSchoolSelections[districtId] = selectedSchoolId
                    } else {
                        updatedSchoolSelections.remove(districtId)
                    }
                    districtContext = districtContext.copy(
                        schoolsByDistrictId = updatedSchoolsByDistrictId,
                        selectedSchoolIdByDistrictId = updatedSchoolSelections,
                    )
                },
                onSchoolSaved = {
                    scope.launch {
                        loadCurrentOperatorContext()
                    }
                },
            )

            AdminRoute.Users -> UsersScreen(
                apiClient = apiClient,
                district = currentDistrict,
                schools = availableSchools,
                districtLoading = districtContext.isLoading,
                districtLoadError = districtContext.error,
                theme = theme,
                canEdit = districtAdminAccess,
            )
        }
        }
    }
}
